import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { ArrowLeft } from "lucide-react";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useLang();

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login"
        ? { email: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password };
      const { data } = await api.post(path, body);
      setUser(data);
      if (!data.tenant_id || !data.role) navigate("/baslangic");
      else navigate("/uygulama");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/uygulama";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12">
        <Link to="/" className="font-heading text-2xl font-black tracking-tighter" data-testid="auth-brand">alignee</Link>
        <div>
          <h2 className="font-heading text-4xl font-bold tracking-tight leading-tight max-w-sm">
            {t("auth.slogan")}
          </h2>
          <p className="mt-4 text-primary-foreground/70 max-w-sm">
            {t("auth.sloganSub")}
          </p>
        </div>
        <span className="text-primary-foreground/50 text-sm">{t("common.copyright")}</span>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-home-link">
              <ArrowLeft className="w-4 h-4" /> {t("auth.back")}
            </Link>
            <LanguageSwitcher />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            {mode === "login" ? t("auth.welcomeBack") : t("auth.createAccount")}
          </h1>
          <p className="text-muted-foreground mt-2 mb-8">
            {mode === "login" ? t("auth.loginSub") : t("auth.registerSub")}
          </p>

          <Button variant="outline" onClick={googleLogin} className="w-full h-11 rounded-lg mb-4" data-testid="google-login-btn">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            {t("auth.google")}
          </Button>

          <div className="flex items-center gap-3 my-4">
            <div className="h-px bg-border flex-1" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">{t("auth.or")}</span>
            <div className="h-px bg-border flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">{t("auth.name")}</Label>
                <Input id="name" value={form.name} onChange={upd("name")} required placeholder={t("auth.namePh")} className="mt-1.5 h-11" data-testid="name-input" />
              </div>
            )}
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input id="email" type="email" value={form.email} onChange={upd("email")} required placeholder={t("auth.emailPh")} className="mt-1.5 h-11" data-testid="email-input" />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input id="password" type="password" value={form.password} onChange={upd("password")} required placeholder="••••••••" className="mt-1.5 h-11" data-testid="password-input" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 rounded-lg hover:-translate-y-0.5 transition-transform" data-testid="auth-submit-btn">
              {busy ? t("auth.wait") : mode === "login" ? t("auth.login") : t("auth.register")}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-primary font-semibold hover:underline"
              data-testid="toggle-mode-btn"
            >
              {mode === "login" ? t("auth.toRegister") : t("auth.toLogin")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
